import {v4 as uuid} from 'uuid';
import isEmpty from 'lodash/isEmpty';

const choiceTaskPrefix = 'swf::task::decision::';
const parallelTaskPrefix = 'swf::task::parallel::';

const taskTypeToStyle = {
    'Simple': {
        style: 'task',
        width: 120,
        height: 40
    },
    'Http': {
        style: 'http',
        width: 125,
        height: 40
    },
    'JQ': {
        style: 'jq',
        width: 125,
        height: 40
    },
    'Wait': {
        style: 'wait',
        width: 120,
        height: 40
    },
    'Email': {
      style: 'email',
      width: 120,
      height: 40
    },
    'Map': {
      style: 'map',
      width: 120,
      height: 40
    },
    'Terminate': {
        style: 'end',
        width: 30,
        height: 30
    },
    'Start': {
      style: 'start',
      width: 30,
      height: 30
    }
}

const newWorkflow = {
    children: [
      {
          tasks: [],
          routes: [],
      },
    ],
};

const toTask = (model, cell) => {
    const {x, y, width, height} = cell.geometry;

    return {
        id: cell.id,
        ...cell.getValue(),
        outgoingRoutes: model.getEdges(cell, false, true, true).map(cell => cell.id),
        incomingRoutes: model.getEdges(cell, true, false, true).map(cell => cell.id),
        style: cell.style,
        geometry: {
            x,
            y,
            width,
            height
        }
    }
};

const toRoute = cell => {
    const route = cell.getValue();

    return {
        id: cell.id,
        name: route.name && route.name.length > 0 ? route.name : null,
        routingType: route.routingType,
        condition: route.condition,
        source: cell.source.id,
        target: cell.target.id,
        geometry: {
            points: cell.geometry?.points || []
        }
    }
};

const convertCells = (cell, model, tasks, routes) => {
  if (cell.isVertex()) {
    tasks.push(toTask(model, cell));
  } else if (cell.isEdge()) {
    routes.push(toRoute(cell));
  }

  const childCount = cell.getChildCount();
  for (let i = 0; i < childCount; i++) {
    convertCells(cell.getChildAt(i), model, tasks, routes);
  }
}

const toGraph = model => {
    const workflowCell = model.getRoot();
    const {name, description, timeoutSeconds, failureWorkflow, failureWorkflowVersion, maxConcurrency} = workflowCell.getValue();
    const children = [];

    workflowCell.children.forEach(child => {
      const tasks = [];
      const routes = [];
      const startTask = child.getValue()?.startTask;
      const mapTaskId = child.getValue()?.mapTaskId;

      convertCells(child, model, tasks, routes);

      children.push({
        id: child.id,
        mapTaskId,
        startTask,
        tasks,
        routes
      });
    });

    return {
        name,
        description,
        timeoutSeconds,
        failureWorkflow: isEmpty(failureWorkflow) ? null : failureWorkflow,
        failureWorkflowVersion: isEmpty(failureWorkflowVersion) ? null : failureWorkflowVersion,
        maxConcurrency,
        children
    };
};

const processTasks = (cellId, tasks, model, branchJoinTask) => {
    const cell = model.getCell(cellId);
    if (!cell || cellId === branchJoinTask) {
        return;
    }

    const name = cell.getName();
    const {
        description,
        type,
        splitType,
        joinTask,
        inputParameters,
        httpRequest,
        email,
        connectionTimeoutSeconds,
        readTimeoutSeconds,
        credentials,
        filter,
        itemsExpression,
        seconds,
        secondsExpression,
        timestamp,
        timestampExpression,
        terminationStatus,
        retryIntervalSeconds,
        retryMaxAttempts,
        retryBackoffRate,
        maxConcurrency,
        maxConcurrencyExpression,
        workflowOutput
    } = cell.getValue();

    tasks[name] = {
        id: cellId,
        type,
        description,
        inputParameters
    };

    let retry = null;
    if (typeof retryIntervalSeconds !== 'undefined' ||
        typeof retryMaxAttempts !== 'undefined' ||
        typeof retryBackoffRate !== 'undefined') {
        retry = {
            intervalSeconds: retryIntervalSeconds,
            maxAttempts: retryMaxAttempts,
            backoffRate: retryBackoffRate
        }
    }

    if (type === 'Http') {
        tasks[name] = {
            ...tasks[name],
            httpRequest,
            connectionTimeoutSeconds,
            readTimeoutSeconds,
            credentials: isEmpty(credentials) ? null : credentials,
            retry
        }
    } else if (type === 'JQ') {
        tasks[name] = {
            ...tasks[name],
            filter
        }
    } else if (type === 'Wait') {
        tasks[name] = {
            ...tasks[name],
            seconds,
            secondsExpression,
            timestamp,
            timestampExpression
        }
    } else if (type === 'Email') {
      tasks[name] = {
        ...tasks[name],
        email
      }
    } else if (type === 'Terminate') {
        tasks[name] = {
            ...tasks[name],
            terminationStatus,
            workflowOutput
        }
    } else if (type === 'Map') {
      const itemProcessor = model.getRoot().children.find(layer => layer.id === cell.getValue().itemProcessorId);

      const itemProcessorStartTask = itemProcessor.getValue().startTask;
      const itemProcessorTasks = {};

      processTasks(itemProcessorStartTask, itemProcessorTasks, model);

      const mapStartTask = model.getCell(itemProcessorStartTask).getName();
      const mapTasks = isEmpty(itemProcessorTasks[mapStartTask]?.type) ? {} : itemProcessorTasks;

      tasks[name] = {
        ...tasks[name],
        itemsExpression,
        itemProcessor: {
          startTask: mapStartTask,
          tasks: mapTasks
        },
        maxConcurrency,
        maxConcurrencyExpression
      }
    }

    let outgoingRoutes = model.getEdges(cell, false, true, true);

    if (splitType === 'And') {
        const branches = [];

        outgoingRoutes.forEach(outgoingRoute => {
            const branchTasks = {};
            processTasks(outgoingRoute.target.id, branchTasks, model, joinTask);

            if (!isEmpty(branchTasks)) {
                branches.push({
                    id: outgoingRoute.id,
                    startTask: model.getCell(outgoingRoute.target.id).getName(),
                    tasks: branchTasks
                });
            }
        });

        let parallelTaskName = `${parallelTaskPrefix}${name}`;
        if (cell.isStart()) {
          parallelTaskName = name;
        } else {
          tasks[name].nextTask = parallelTaskName;
        }

        tasks[parallelTaskName] = {
            id: cellId,
            type: 'Parallel',
            branches,
            nextTask: model.getCell(joinTask).getName()
        }

        if (cell.isStart()) {
          tasks[parallelTaskName] = {
            ...tasks[parallelTaskName],
            inputParameters
          };
        }

        processTasks(joinTask, tasks, model, branchJoinTask);

        return;
    } else if (outgoingRoutes.length === 0) {
        tasks[name].end = true;
    } else if (outgoingRoutes.length === 1) {
        if (outgoingRoutes[0].target.id === branchJoinTask) {
            tasks[name].end = true;
            return;
        }

        const nextTask = model.getCell(outgoingRoutes[0].target.id).getName();
        if (outgoingRoutes[0].isConditional()) {
            const decisionTaskName = `${choiceTaskPrefix}${name}`;
            tasks[decisionTaskName] = {
                id: uuid(),
                type: 'Decision',
                choices: [{
                    id: outgoingRoutes[0].id,
                    condition: outgoingRoutes[0].value?.condition,
                    nextTask,
                }]
            };

            tasks[name].nextTask = decisionTaskName;
        } else {
            tasks[name].nextTask = nextTask;
        }
    } else {
        const choices = [];
        let defaultTask;
        outgoingRoutes.forEach(outgoingRoute => {
            const nextTask = model.getCell(outgoingRoute.target.id).getName();
            if (outgoingRoute.isConditional()) {
                choices.push({
                    id: outgoingRoute.id,
                    condition: outgoingRoute.value?.condition,
                    nextTask
                });
            } else if (!defaultTask) {
                defaultTask = nextTask;
            }
        });

        let decisionTaskName = `${choiceTaskPrefix}${name}`;
        if (cell.isStart()) {
          decisionTaskName = name;
        } else {
          tasks[name].nextTask = decisionTaskName;
        }

        tasks[decisionTaskName] = {
            id: uuid(),
            type: 'Decision',
            defaultTask
        };

        if (cell.isStart()) {
          tasks[decisionTaskName] = {
            ...tasks[decisionTaskName],
            inputParameters
          };
        }

        if (!isEmpty(choices)) {
          tasks[decisionTaskName] = {
            ...tasks[decisionTaskName],
            choices
          };
        }
    }

    outgoingRoutes.forEach(outgoingRoute => {
        const nextTask = model.getCell(outgoingRoute.target.id).getName();
        if (!tasks.hasOwnProperty(nextTask) && outgoingRoute.target.id !== branchJoinTask) {
            processTasks(outgoingRoute.target.id, tasks, model, branchJoinTask);
        }
    });
}

const processTaskDefinitions = (name, taskDefinitions, children, childrenIndex, taskIdMap, taskProcessors) => {
    if (taskIdMap.has(name)) {
        return;
    }

    taskIdMap.set(name, uuid());

    const {tasks, routes} = children[childrenIndex];

    const {
        description,
        inputParameters,
        httpRequest,
        email,
        connectionTimeoutSeconds,
        readTimeoutSeconds,
        credentials,
        seconds,
        secondsExpression,
        filter,
        itemsExpression,
        itemProcessor,
        timestamp,
        timestampExpression,
        type,
        terminationStatus,
        workflowOutput,
        nextTask,
        retry,
        maxConcurrency,
        maxConcurrencyExpression,
        choices,
        defaultTask,
        branches
    } = taskDefinitions[name];

    if (['Simple', 'Http', 'JQ', 'Email', 'Wait', 'Map', 'Terminate'].includes(type)) {
        const task = {
            id: taskIdMap.get(name),
            name,
            description,
            inputParameters,
            joinTask: null,
            joinType: 'Or',
            processor: taskProcessors[name],
            splitType: 'Or',
            type
        }

        const taskRetry = {
            retryIntervalSeconds: retry?.intervalSeconds,
            retryMaxAttempts: retry?.maxAttempts,
            retryBackoffRate: retry?.backoffRate,
        }

        if (type === 'Http') {
            tasks.push({
                ...task,
                httpRequest,
                connectionTimeoutSeconds,
                readTimeoutSeconds,
                credentials,
                ...taskRetry
            });
        } else if (type === 'JQ') {
            tasks.push({
                ...task,
                filter,
                ...taskRetry
            });
        } else if (type === 'Email') {
          tasks.push({
            ...task,
            email
          });
        } else if (type === 'Wait') {
            tasks.push({
                ...task,
                seconds,
                secondsExpression,
                timestamp,
                timestampExpression
            });
        } else if (type === 'Terminate') {
          tasks.push({
            ...task,
            workflowOutput,
            terminationStatus
          });
        } else if (type === 'Map') {
          tasks.push({
            ...task,
            itemsExpression,
            itemProcessorId: uuid(),
            maxConcurrency,
            maxConcurrencyExpression
          });

          children.push({
            tasks: [],
            routes: []
          });

          processTaskDefinitions(itemProcessor.startTask, itemProcessor.tasks, children, childrenIndex + 1, taskIdMap, {});

          children[childrenIndex + 1].startTask = taskIdMap.get(itemProcessor.startTask);
        } else {
            tasks.push(task);
        }

        if (nextTask) {
            processTaskDefinitions(nextTask, taskDefinitions, children, childrenIndex, taskIdMap, taskProcessors);

            if (taskIdMap.has(nextTask)) {
                routes.push({
                    id: uuid(),
                    name: null,
                    source: taskIdMap.get(name),
                    target: taskIdMap.get(nextTask),
                    routingType: 'UnConditional'
                });
            }
        }
    } else if (type === 'Decision') {
        let lastTask = tasks[tasks.length - 1];
        if (lastTask) {
          taskIdMap.delete(name);
        } else {
          lastTask = {
            id: taskIdMap.get(name),
            name,
            description,
            inputParameters,
            splitType: 'Or',
            style: 'start'
          };

          tasks.push(lastTask);
        }

        if (defaultTask) {
            processTaskDefinitions(defaultTask, taskDefinitions, children, childrenIndex, taskIdMap, taskProcessors);

            if (taskIdMap.has(defaultTask)) {
                routes.push({
                    id: uuid(),
                    name: null,
                    source: taskIdMap.get(lastTask?.name),
                    target: taskIdMap.get(defaultTask),
                    routingType: 'UnConditional'
                });
            }
        }

        choices.forEach(choice => {
            processTaskDefinitions(choice.nextTask, taskDefinitions, children, childrenIndex, taskIdMap, taskProcessors);

            if (taskIdMap.has(choice.nextTask)) {
                routes.push({
                    id: uuid(),
                    name: null,
                    condition: choice.condition,
                    source: taskIdMap.get(lastTask?.name),
                    target: taskIdMap.get(choice.nextTask),
                    routingType: 'Conditional',
                });
            }
        });
    } else if (type === 'Parallel') {
        let lastTask = tasks[tasks.length - 1];
        if (lastTask) {
          lastTask.splitType = 'And';
          taskIdMap.delete(name);
        } else {
          lastTask = {
            id: taskIdMap.get(name),
            name,
            description,
            inputParameters,
            splitType: 'And',
            style: 'start'
          };

          tasks.push(lastTask);
        }

        let joinTask;
        if (nextTask) {
          processTaskDefinitions(nextTask, taskDefinitions, children, childrenIndex, taskIdMap, taskProcessors);

          joinTask = tasks.find(t => t.name === nextTask);
          joinTask.joinType = 'And';

          if (lastTask) {
            lastTask.joinTask = joinTask.id;
          }
        }

        branches.forEach(branch => {
            const branchChildren = [
              {
                tasks: [],
                routes: []
              }
            ];
            processTaskDefinitions(branch.startTask, branch.tasks, branchChildren, childrenIndex, taskIdMap, taskProcessors);

            const {tasks: branchTasks, routes: branchRoutes} = branchChildren[0];

            tasks.push(...branchTasks);
            routes.push(...branchRoutes);

            if (taskIdMap.has(branch.startTask)) {
                routes.push({
                    id: uuid(),
                    name: null,
                    source: taskIdMap.get(lastTask?.name),
                    target: taskIdMap.get(branch.startTask),
                    routingType: 'UnConditional'
                });
            }

            if (joinTask && branchTasks[branchTasks.length - 1].type !== 'Terminate') {
                routes.push({
                    id: uuid(),
                    name: null,
                    source: taskIdMap.get(branchTasks[branchTasks.length - 1].name),
                    target: joinTask.id,
                    routingType: 'UnConditional'
                });
            }
        });
    }
}

const fromWorkflowDefinition = (workflowDefinition, taskProcessors = {}) => {
    const {name, description, startTask, timeoutSeconds, failureWorkflow, failureWorkflowVersion, maxConcurrency} = workflowDefinition;
    const children = [
      {
        tasks: [],
        routes: [],
      },
    ];
    const taskIdMap = new Map();

    processTaskDefinitions(startTask, workflowDefinition.tasks, children, 0, taskIdMap, taskProcessors);

    children[0].startTask = taskIdMap.get(startTask);

    return {
        name,
        description,
        timeoutSeconds,
        failureWorkflow,
        failureWorkflowVersion,
      maxConcurrency,
        children
    };
}

const toWorkflowDefinition = model => {
    const {name, description, timeoutSeconds, failureWorkflow, failureWorkflowVersion, maxConcurrency} = model.getRoot().getValue();
    const {startTask: startCellId} = model.getRoot().children[0].getValue();
    let tasks = {};

    processTasks(startCellId, tasks, model);

    if (Object.keys(tasks).length === 1 && !Object.values(tasks)[0].type) {
        tasks = {};
    }

    return {
        name,
        description,
        startTask: model.getCell(startCellId).getName(),
        timeoutSeconds,
        failureWorkflow: isEmpty(failureWorkflow) ? null : failureWorkflow,
        failureWorkflowVersion: isEmpty(failureWorkflowVersion) ? null : failureWorkflowVersion,
        maxConcurrency,
        tasks
    }
}

const toTaskProcessors = model => {
    const workflowCell = model.getRoot();
    const toTaskProcessors = {};

    (function convertCells(cell) {
        if (cell.isVertex()) {
            const value = cell.getValue();
            if (!isEmpty(value.processor)) {
                toTaskProcessors[cell.getName()] = value.processor;
            }
        }

        const childCount = cell.getChildCount();
        for (let i = 0; i < childCount; i++) {
            convertCells(cell.getChildAt(i));
        }
    })(workflowCell);

    return toTaskProcessors;
};

export {
    toGraph,
    toWorkflowDefinition,
    fromWorkflowDefinition,
    toTaskProcessors,
    taskTypeToStyle,
    newWorkflow,
    choiceTaskPrefix,
    parallelTaskPrefix
};